<template>
    <div ref="cardContainerRef" :style="cardContainerStyles">
        <div
        class="cards-container"
        :key="selectedTab"
        :style="cardWrapperStyles"
        v-dragscroll
        @dragscrollmove="scrollHandler">
            <div ref="beforeRef" class="before"></div>
            <!-- products cards components -->
            <products-cards
            :colors="colors"
            :products="products"
            :selectedTab="selectedTab" />
            <div class="after"></div>
        </div>
    </div>
</template>

<script setup>
import ProductsCards from '@/components/features/home/widgets/patrimonyTabChart/mobile/Cards'

/* Vue */
import { ref, watch, onMounted, onUnmounted } from 'vue'

/* Assets */
import { validateIfParentExists } from '@/assets/scripts/htmlManipulators.js'

/** Props
@products {array} - array with products
@colors {array} - array with products colors
@selectedTab {string} - active tab description
*/
const props = defineProps({
    products: Array,
    colors: Array,
    selectedTab: String
})

const emit = defineEmits(['scrolledToCard'])

/* DOM Refs */
const cardContainerRef = ref()

/****************************************************
*    Logic to get parent and align our component    *
****************************************************/
const parentForReference = ref(null)
const cardContainerStyles = ref(null)
const cardWrapperStyles = ref(null)

const getParentForReferenceWidth = () => {
    parentForReference.value = validateIfParentExists(cardContainerRef.value, 'full-screen-helper-aligner-container')

    if (parentForReference.value) {
        const PADDING_LEFT = 24
        const PADDING_RIGHT = 24
        const sizeDiff = parentForReference.value.clientWidth - parentForReference.value.children[0].clientWidth

        cardContainerStyles.value = {
            width: `${parentForReference.value.clientWidth}px`,
            'margin-left': `${-(sizeDiff / 2)}px`,
            'margin-right': `${-(sizeDiff / 2)}px`,
            'padding-left': `${(sizeDiff / 2) - PADDING_LEFT}px`,
            'padding-right': `${(sizeDiff / 2) - PADDING_RIGHT}px`
        }

        cardWrapperStyles.value = {
            'min-width': `${parentForReference.value.clientWidth}px`,
            'padding-left': `${(sizeDiff / 2)}px`,
            'padding-right': `${(sizeDiff / 2)}px`
        }
    }
}

/**********************************************************************
*                                                                     *
*                            Scroll Logic                             *
*                                                                     *
**********************************************************************/
const beforeRef = ref()

const scrollHandler = () => {
    const scrolledValue = cardContainerRef.value.children[0].scrollLeft / 248
    const index = Math.round(scrolledValue)

    if (!props.products.length || !props.products[index]) return

    cardContainerRef.value.children[0].scrollTo({
        /* 400 is the magic number to center the card (it's a random number, but it works) */
        left: ((248 + 16) * index) - (beforeRef.value.clientWidth / 400),
        behavior: 'smooth'
    })

    scrolledToCardEmit(
        props.products[index].description,
        props.products[index].percentage,
        props.products[index].parsedPercentage,
        props.colors[index]
    )
}

const scrolledToCardEmit = (id, percentage, parsedPercentage, color) => {
    emit('scrolledToCard', {
        id: id,
        percentage: percentage,
        parsedPercentage: parsedPercentage,
        percentageColor: color
    })
}

const resetToFirstCard = () => {
    if (!props.products.length) return

    scrolledToCardEmit(
        props.products[0].description,
        props.products[0].percentage,
        props.products[0].parsedPercentage,
        props.colors[0]
    )
}

watch(() => props.selectedTab, () => {
    resetToFirstCard()
})

watch(() => props.products, () => {
    resetToFirstCard()
})

onMounted(() => {
    getParentForReferenceWidth()
    window.addEventListener('resize', getParentForReferenceWidth)
    resetToFirstCard()
})

onUnmounted(() => {
    window.removeEventListener('resize', getParentForReferenceWidth)
})
</script>

<style lang="scss" scoped>
.cards-container {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 16px;

    overflow: hidden;

    .before,
    .after {
        width: calc((100% - 248px - 32px) / 2);
        height: 126px;
        flex-shrink: 0;
    }
}
</style>
